import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { getUser } from "../graphql/queries";
import { ChangeLogChangeType, ChangeLogEntityType } from "../API";
import {
  createUserPreferencesDocument,
  updateUserPreferencesDocument,
} from "../graphql/customQueries";
import {
  type ChangeLogEntryInput,
  createChangeRequestId,
  logChanges,
} from "../graphql/fetchers";
import { useUser } from "../contexts/UserContext";
import { useRecipients } from "../graphql/hooks";
import { useNavigationGuard } from "../contexts/NavigationGuardContext";
import {
  FieldErrorMap,
  FieldValidators,
  requiredFieldValidator,
  trimFormValues,
  useUnsavedChangesModalGuard,
  validateFormFields,
} from "../utilities/formValidation";
import {
  convertUtcToLocal,
  getUserFriendlyErrorMessage,
  logToConsole,
} from "../utilities/utilities";
import { queryKeys } from "../graphql/queryKeys";
import FormFieldRow from "./FormFieldRow";
import ConfirmationModal from "./ui/ConfirmationModal";
import BlockingPageLoader from "./ui/BlockingPageLoader";
import "../styleSheets/profile.css";
import "../styleSheets/buttons.css";
import "../styleSheets/commonElements.css";
import "../styleSheets/formFields.css";

type PreferencesFormValues = {
  eolVerificationIntervalDays: string;
  eolVerificationReminderCount: string;
  eolVerificationReminderIntervalDays: string;
  eolVerificationChannelSmsEnabled: string;
  eolStopScheduledMessages: string;
  minBodyWordCount: string;
  eolAccountReaderRecipientId: string;
  preferredLanguage: string;
};

type PreferencesAuditSnapshot = {
  eolVerificationIntervalDays: number;
  eolVerificationReminderCount: number;
  eolVerificationReminderIntervalDays: number;
  eolVerificationChannelSmsEnabled: boolean;
  eolStopScheduledMessages: boolean;
  minBodyWordCount: number;
  eolAccountReaderRecipientId: string | null;
  preferredLanguage: string;
};

type PreferencesChangedFields = Partial<
  Record<
    keyof PreferencesAuditSnapshot,
    {
      old: string | number | boolean | null;
      new: string | number | boolean | null;
    }
  >
>;

const client = generateClient();

const emptyForm: PreferencesFormValues = {
  eolVerificationIntervalDays: "30",
  eolVerificationReminderCount: "2",
  eolVerificationReminderIntervalDays: "1",
  eolVerificationChannelSmsEnabled: "false",
  eolStopScheduledMessages: "false",
  minBodyWordCount: "25",
  eolAccountReaderRecipientId: "",
  preferredLanguage: "en",
};

const allowedIntervals = new Set(["30", "60", "90", "180", "360"]);
const allowedReminderCounts = new Set(["2", "5", "10"]);
const allowedReminderIntervals = new Set(["1", "2", "5", "10", "15"]);
const allowedLanguages = new Set(["en", "es"]);

const PREFERENCES_CHANGE_LOG_METADATA = {
  source: "UI",
  page: "Preferences",
  device: "web",
} as const;

function normalizePreferredLanguage(value: string | null | undefined): string {
  return String(value ?? emptyForm.preferredLanguage)
    .trim()
    .toLowerCase() === "es"
    ? "es"
    : "en";
}

function getPreferencesAuditSnapshot(
  values: Partial<PreferencesFormValues> | null | undefined,
): PreferencesAuditSnapshot {
  const trimmedValues = trimFormValues({
    eolVerificationIntervalDays:
      values?.eolVerificationIntervalDays ??
      emptyForm.eolVerificationIntervalDays,
    eolVerificationReminderCount:
      values?.eolVerificationReminderCount ??
      emptyForm.eolVerificationReminderCount,
    eolVerificationReminderIntervalDays:
      values?.eolVerificationReminderIntervalDays ??
      emptyForm.eolVerificationReminderIntervalDays,
    eolVerificationChannelSmsEnabled:
      values?.eolVerificationChannelSmsEnabled ??
      emptyForm.eolVerificationChannelSmsEnabled,
    eolStopScheduledMessages:
      values?.eolStopScheduledMessages ?? emptyForm.eolStopScheduledMessages,
    minBodyWordCount: values?.minBodyWordCount ?? emptyForm.minBodyWordCount,
    eolAccountReaderRecipientId: values?.eolAccountReaderRecipientId ?? "",
    preferredLanguage: values?.preferredLanguage ?? emptyForm.preferredLanguage,
  });

  return {
    eolVerificationIntervalDays: Number(
      trimmedValues.eolVerificationIntervalDays,
    ),
    eolVerificationReminderCount: Number(
      trimmedValues.eolVerificationReminderCount,
    ),
    eolVerificationReminderIntervalDays: Number(
      trimmedValues.eolVerificationReminderIntervalDays,
    ),
    eolVerificationChannelSmsEnabled:
      trimmedValues.eolVerificationChannelSmsEnabled === "true",
    eolStopScheduledMessages: trimmedValues.eolStopScheduledMessages === "true",
    minBodyWordCount: Number(trimmedValues.minBodyWordCount),
    eolAccountReaderRecipientId:
      trimmedValues.eolAccountReaderRecipientId || null,
    preferredLanguage: normalizePreferredLanguage(
      trimmedValues.preferredLanguage,
    ),
  };
}

function getChangedPreferencesFields(
  before: PreferencesAuditSnapshot,
  after: PreferencesAuditSnapshot,
): PreferencesChangedFields {
  const changedFields: PreferencesChangedFields = {};

  if (
    before.eolVerificationIntervalDays !== after.eolVerificationIntervalDays
  ) {
    changedFields.eolVerificationIntervalDays = {
      old: before.eolVerificationIntervalDays,
      new: after.eolVerificationIntervalDays,
    };
  }

  if (
    before.eolVerificationReminderCount !== after.eolVerificationReminderCount
  ) {
    changedFields.eolVerificationReminderCount = {
      old: before.eolVerificationReminderCount,
      new: after.eolVerificationReminderCount,
    };
  }

  if (
    before.eolVerificationReminderIntervalDays !==
    after.eolVerificationReminderIntervalDays
  ) {
    changedFields.eolVerificationReminderIntervalDays = {
      old: before.eolVerificationReminderIntervalDays,
      new: after.eolVerificationReminderIntervalDays,
    };
  }

  if (
    before.eolVerificationChannelSmsEnabled !==
    after.eolVerificationChannelSmsEnabled
  ) {
    changedFields.eolVerificationChannelSmsEnabled = {
      old: before.eolVerificationChannelSmsEnabled,
      new: after.eolVerificationChannelSmsEnabled,
    };
  }

  if (before.eolStopScheduledMessages !== after.eolStopScheduledMessages) {
    changedFields.eolStopScheduledMessages = {
      old: before.eolStopScheduledMessages,
      new: after.eolStopScheduledMessages,
    };
  }

  if (before.minBodyWordCount !== after.minBodyWordCount) {
    changedFields.minBodyWordCount = {
      old: before.minBodyWordCount,
      new: after.minBodyWordCount,
    };
  }

  if (
    before.eolAccountReaderRecipientId !== after.eolAccountReaderRecipientId
  ) {
    changedFields.eolAccountReaderRecipientId = {
      old: before.eolAccountReaderRecipientId,
      new: after.eolAccountReaderRecipientId,
    };
  }

  if (before.preferredLanguage !== after.preferredLanguage) {
    changedFields.preferredLanguage = {
      old: before.preferredLanguage,
      new: after.preferredLanguage,
    };
  }

  return changedFields;
}

function Preferences() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["common", "tipToolHelp"]);
  const { setGuard } = useNavigationGuard();
  const { user, isAuthenticated } = useUser();
  const userId = user?.userId ?? "";
  const queryClient = useQueryClient();

  const recipientsQ = useRecipients(userId, isAuthenticated && !!userId);
  const recipients = useMemo(() => recipientsQ.data ?? [], [recipientsQ.data]);
  const recipientIds = useMemo(
    () => new Set(recipients.map((r) => String(r.id || "").trim())),
    [recipients],
  );

  const [preferencesId, setPreferencesId] = useState("");
  const [hasExistingPreferencesRecord, setHasExistingPreferencesRecord] =
    useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");
  const [formValues, setFormValues] =
    useState<PreferencesFormValues>(emptyForm);
  const [initialValues, setInitialValues] =
    useState<PreferencesFormValues | null>(null);
  const auditBaselineSnapshotRef = useRef<PreferencesAuditSnapshot | null>(
    null,
  );
  const [fieldErrors, setFieldErrors] = useState<
    FieldErrorMap<PreferencesFormValues>
  >({});

  const [isSaveResultModalOpen, setIsSaveResultModalOpen] = useState(false);
  const [saveResultTitle, setSaveResultTitle] = useState("");
  const [saveResultMessage, setSaveResultMessage] = useState("");
  const [saveShouldReturnToDashboard, setSaveShouldReturnToDashboard] =
    useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  const normalizedValues = useMemo(
    () => trimFormValues(formValues),
    [formValues],
  );
  const hasUnsavedChanges = useMemo(() => {
    if (!initialValues) return false;
    return JSON.stringify(normalizedValues) !== JSON.stringify(initialValues);
  }, [initialValues, normalizedValues]);

  const {
    isPromptOpen: showUnsavedDialog,
    requestNavigation,
    confirmNavigation,
    cancelNavigation: handleContinueEditing,
  } = useUnsavedChangesModalGuard(hasUnsavedChanges);

  useEffect(() => {
    setGuard(hasUnsavedChanges ? requestNavigation : null);
    return () => setGuard(null);
  }, [hasUnsavedChanges, requestNavigation, setGuard]);

  const validators: FieldValidators<PreferencesFormValues> = useMemo(
    () => ({
      eolVerificationIntervalDays: [
        requiredFieldValidator(t("preferences.validation.intervalRequired")),
        (value: string) =>
          allowedIntervals.has(value)
            ? null
            : t("preferences.validation.intervalInvalid"),
      ],
      eolVerificationReminderCount: [
        requiredFieldValidator(
          t("preferences.validation.reminderCountRequired"),
        ),
        (value: string) =>
          allowedReminderCounts.has(value)
            ? null
            : t("preferences.validation.reminderCountInvalid"),
      ],
      eolVerificationReminderIntervalDays: [
        requiredFieldValidator(
          t("preferences.validation.reminderIntervalRequired"),
        ),
        (value: string) =>
          allowedReminderIntervals.has(value)
            ? null
            : t("preferences.validation.reminderIntervalInvalid"),
      ],
      minBodyWordCount: [
        requiredFieldValidator(
          t("preferences.validation.minimumWordsRequired"),
        ),
        (value: string) => {
          const parsed = Number(value);
          if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
            return t("preferences.validation.minimumWordsInvalid");
          }
          return parsed >= 20
            ? null
            : t("preferences.validation.minimumWordsTooSmall");
        },
      ],
      eolAccountReaderRecipientId: [
        (value: string) =>
          !value || recipientIds.has(value)
            ? null
            : t("preferences.validation.accountReaderInvalid"),
      ],
      preferredLanguage: [
        requiredFieldValidator(t("preferences.validation.languageRequired")),
        (value: string) =>
          allowedLanguages.has(value)
            ? null
            : t("preferences.validation.languageInvalid"),
      ],
    }),
    [recipientIds, t],
  );

  useEffect(() => {
    const loadPreferences = async () => {
      if (!isAuthenticated || !userId) return;

      setIsLoading(true);
      setLoadError("");

      try {
        const result: any = await client.graphql({
          query: getUser,
          variables: { id: userId },
        });

        const currentUser = result?.data?.getUser;
        const preferences = currentUser?.UserPreferences;

        const fetchedValues: PreferencesFormValues = {
          eolVerificationIntervalDays: String(
            preferences?.eolVerificationIntervalDays ??
              emptyForm.eolVerificationIntervalDays,
          ),
          eolVerificationReminderCount: String(
            preferences?.eolVerificationReminderCount ??
              emptyForm.eolVerificationReminderCount,
          ),
          eolVerificationReminderIntervalDays: String(
            preferences?.eolVerificationReminderIntervalDays ??
              emptyForm.eolVerificationReminderIntervalDays,
          ),
          eolVerificationChannelSmsEnabled:
            preferences?.eolVerificationChannelSmsEnabled ? "true" : "false",
          eolStopScheduledMessages: preferences?.eolStopScheduledMessages
            ? "true"
            : "false",
          minBodyWordCount: String(
            preferences?.minBodyWordCount ?? emptyForm.minBodyWordCount,
          ),
          eolAccountReaderRecipientId:
            preferences?.eolAccountReaderRecipientId ?? "",
          preferredLanguage:
            String(
              preferences?.preferredLanguage || emptyForm.preferredLanguage,
            )
              .trim()
              .toLowerCase() === "es"
              ? "es"
              : "en",
        };

        const normalizedFetchedValues = trimFormValues(fetchedValues);
        auditBaselineSnapshotRef.current = getPreferencesAuditSnapshot(
          normalizedFetchedValues,
        );
        setPreferencesId(preferences?.id ?? userId);
        setHasExistingPreferencesRecord(!!preferences?.id);
        setFormValues(normalizedFetchedValues);
        setInitialValues(normalizedFetchedValues);
        setRegisteredEmail(
          String(
            currentUser?.email ?? user?.signInDetails?.loginId ?? "",
          ).trim(),
        );
        setLastUpdatedAt(preferences?.updatedAt ?? "");
        setFieldErrors({});
      } catch (error) {
        logToConsole("error", "Error loading preferences:", error);
        setLoadError(getUserFriendlyErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    };

    void loadPreferences();
  }, [isAuthenticated, user?.signInDetails?.loginId, userId]);

  const handleFieldChange = (
    fieldName: keyof PreferencesFormValues,
    value: string,
  ) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: value }));
    if (fieldErrors[fieldName]) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    }
  };

  const handleSavePreferences = async () => {
    if (!preferencesId) return;

    if (!hasUnsavedChanges) {
      setSaveShouldReturnToDashboard(true);
      setSaveResultTitle(t("errorMessage.noChangesTitle"));
      setSaveResultMessage(t("errorMessage.noChangesToSave"));
      setIsSaveResultModalOpen(true);
      return;
    }

    const trimmedValues = trimFormValues(formValues);
    const validationErrors = validateFormFields(trimmedValues, validators);
    setFieldErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSaving(true);

    try {
      const beforeAuditSnapshot = auditBaselineSnapshotRef.current;
      const saveRequestId = createChangeRequestId();
      const input = {
        id: preferencesId,
        eolVerificationIntervalDays: Number(
          trimmedValues.eolVerificationIntervalDays,
        ),
        eolVerificationReminderCount: Number(
          trimmedValues.eolVerificationReminderCount,
        ),
        eolVerificationReminderIntervalDays: Number(
          trimmedValues.eolVerificationReminderIntervalDays,
        ),
        eolVerificationChannelEmailEnabled: true,
        eolVerificationChannelSmsEnabled:
          trimmedValues.eolVerificationChannelSmsEnabled === "true",
        eolStopScheduledMessages:
          trimmedValues.eolStopScheduledMessages === "true",
        minBodyWordCount: Number(trimmedValues.minBodyWordCount),
        eolAccountReaderRecipientId:
          trimmedValues.eolAccountReaderRecipientId || null,
        preferredLanguage: trimmedValues.preferredLanguage,
      };

      const saveResult: any = await client.graphql({
        query: hasExistingPreferencesRecord
          ? updateUserPreferencesDocument
          : createUserPreferencesDocument,
        variables: { input },
      });

      const savedValues: PreferencesFormValues = {
        eolVerificationIntervalDays: String(input.eolVerificationIntervalDays),
        eolVerificationReminderCount: String(
          input.eolVerificationReminderCount,
        ),
        eolVerificationReminderIntervalDays: String(
          input.eolVerificationReminderIntervalDays,
        ),
        eolVerificationChannelSmsEnabled: input.eolVerificationChannelSmsEnabled
          ? "true"
          : "false",
        eolStopScheduledMessages: input.eolStopScheduledMessages
          ? "true"
          : "false",
        minBodyWordCount: String(input.minBodyWordCount),
        eolAccountReaderRecipientId: input.eolAccountReaderRecipientId ?? "",
        preferredLanguage: normalizePreferredLanguage(input.preferredLanguage),
      };
      const normalizedSavedValues = trimFormValues(savedValues);
      const savedPreferredLanguage = normalizePreferredLanguage(
        input.preferredLanguage,
      );
      const savedAuditSnapshot = getPreferencesAuditSnapshot(
        normalizedSavedValues,
      );
      const savedPreferencesId =
        saveResult?.data?.createUserPreferences?.id ??
        saveResult?.data?.updateUserPreferences?.id ??
        preferencesId;

      window.localStorage.setItem("mtam.language", savedPreferredLanguage);
      const currentLanguage = i18n.language?.toLowerCase().startsWith("es")
        ? "es"
        : "en";
      if (savedPreferredLanguage !== currentLanguage) {
        await i18n.changeLanguage(savedPreferredLanguage);
      }

      await queryClient.invalidateQueries({
        queryKey: queryKeys.userSettings(userId),
      });

      if (hasExistingPreferencesRecord) {
        const updateAuditEntries: ChangeLogEntryInput[] = [
          {
            requestId: saveRequestId,
            entityType: ChangeLogEntityType.PREFERENCES,
            entityId: savedPreferencesId,
            changeType: ChangeLogChangeType.UPDATE,
            metadata: PREFERENCES_CHANGE_LOG_METADATA,
            userId,
          },
        ];

        if (beforeAuditSnapshot) {
          const changedFields = getChangedPreferencesFields(
            beforeAuditSnapshot,
            savedAuditSnapshot,
          );

          (
            Object.entries(changedFields) as Array<
              [
                keyof PreferencesChangedFields,
                PreferencesChangedFields[keyof PreferencesChangedFields],
              ]
            >
          ).forEach(([fieldName, change]) => {
            if (!change) return;

            updateAuditEntries.push({
              requestId: saveRequestId,
              entityType: ChangeLogEntityType.PREFERENCES,
              entityId: savedPreferencesId,
              changeType: ChangeLogChangeType.UPDATE,
              fieldName,
              oldValue: change.old,
              newValue: change.new,
              metadata: PREFERENCES_CHANGE_LOG_METADATA,
              userId,
            });
          });
        }

        void logChanges(updateAuditEntries).catch(() => undefined);
      } else {
        void logChanges([
          {
            requestId: saveRequestId,
            entityType: ChangeLogEntityType.PREFERENCES,
            entityId: savedPreferencesId,
            changeType: ChangeLogChangeType.CREATE,
            metadata: PREFERENCES_CHANGE_LOG_METADATA,
            userId,
          },
        ]).catch(() => undefined);
      }

      auditBaselineSnapshotRef.current = savedAuditSnapshot;
      setPreferencesId(savedPreferencesId);
      setFormValues(normalizedSavedValues);
      setInitialValues(normalizedSavedValues);
      setHasExistingPreferencesRecord(true);
      setLastUpdatedAt(new Date().toISOString());
      setFieldErrors({});
      setSaveShouldReturnToDashboard(true);
      setSaveResultTitle(t("preferences.validation.updateSuccessTitle"));
      setSaveResultMessage(t("preferences.validation.updateSuccess"));
      setIsSaveResultModalOpen(true);
    } catch (error) {
      logToConsole("error", "Error saving preferences:", error);
      setSaveShouldReturnToDashboard(false);
      setSaveResultTitle(t("preferences.validation.updateFailedTitle"));
      setSaveResultMessage(t("preferences.validation.updateFailed"));
      setIsSaveResultModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDiscardChanges = () => {
    if (hasUnsavedChanges && hasExistingPreferencesRecord && preferencesId) {
      void logChanges([
        {
          requestId: createChangeRequestId(),
          entityType: ChangeLogEntityType.PREFERENCES,
          entityId: preferencesId,
          changeType: ChangeLogChangeType.ABANDON,
          metadata: PREFERENCES_CHANGE_LOG_METADATA,
          userId,
        },
      ]).catch(() => undefined);
    }

    confirmNavigation();
  };

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/"
        replace
        state={{ toast: { type: "info", message: t("common.signIn") } }}
      />
    );
  }

  if (isLoading) {
    return <BlockingPageLoader message={t("preferences.loading")} />;
  }

  if (isSaving) {
    return <BlockingPageLoader message={t("common.saving")} />;
  }

  if (loadError) {
    return (
      <div className="profile-page">
        <div className="profile-content">
          <div className="profile-banner profile-banner--error">
            {loadError}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-topbar">
        <div className="profile-topbar__nav">
          <div className="profile-topbar__container">
            <div className="profile-topbar__row">
              <button
                type="button"
                className="btn btn--gray btn--size-md btn--icon-left"
                onClick={() => requestNavigation(() => navigate("/Dashboard"))}
              >
                <div
                  className="btn__icon icon-chevron-left"
                  aria-hidden="true"
                />
                {t("common.cancel")}
              </button>

              <div className="profile-topbar__title">
                {t("header.menu.userSettings")}
              </div>
              <div className="profile-topbar__actions">
                <button
                  type="button"
                  className="btn btn--green btn--size-md btn--icon-left"
                  onClick={handleSavePreferences}
                  disabled={isSaving}
                >
                  <span className="btn__icon icon-check" aria-hidden="true" />
                  {isSaving ? t("common.saving") : t("common.save")}
                </button>
              </div>
            </div>
            <div className="profile-topbar__meta">
              {lastUpdatedAt
                ? `${t("common.lastUpdated")}: ${convertUtcToLocal(
                    lastUpdatedAt,
                    "DateOnly",
                  )}`
                : ""}
            </div>
            <div className="profile-topbar__titleBelow">
              <div className="profile-topbar__titleBelowMain">
                {t("header.menu.userSettings")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-content">
        <section className="profile-introCard">
          <div className="profile-introCard__title">
            {t("preferences.title")}
          </div>
          <p className="profile-introCard__text">{t("preferences.subTitle")}</p>
        </section>

        <section className="profile-sectionCard">
          <div className="profile-sectionCard__title">
            {t("preferences.verificationSettings")}
          </div>

          <div className="profile-grid">
            <FormFieldRow
              id="preferencesEmail"
              label={t("preferences.emailChannel")}
              isWritten={registeredEmail.length > 0}
              readOnly
              tooltip={t("preferences.emailVerification", {
                ns: "tipToolHelp",
              })}
            >
              <div id="preferencesEmail" className="profile-emailValue">
                {registeredEmail || t("profile.noEmail")}
              </div>
            </FormFieldRow>

            <FormFieldRow
              id="eolVerificationIntervalDays"
              label={t("preferences.intervalDays")}
              required
              isWritten={
                normalizedValues.eolVerificationIntervalDays.length > 0
              }
              tooltip={t("preferences.intervalDays", {
                ns: "tipToolHelp",
              })}
              error={fieldErrors.eolVerificationIntervalDays}
            >
              <select
                id="eolVerificationIntervalDays"
                className="profile-select"
                value={formValues.eolVerificationIntervalDays}
                onChange={(event) =>
                  handleFieldChange(
                    "eolVerificationIntervalDays",
                    event.target.value,
                  )
                }
              >
                <option value="30">30 {t("preferences.days")}</option>
                <option value="60">60 {t("preferences.days")}</option>
                <option value="90">90 {t("preferences.days")}</option>
                <option value="180">180 {t("preferences.days")}</option>
                <option value="360">360 {t("preferences.days")}</option>
              </select>
            </FormFieldRow>

            <FormFieldRow
              id="eolVerificationReminderCount"
              label={t("preferences.reminderCount")}
              required
              isWritten={
                normalizedValues.eolVerificationReminderCount.length > 0
              }
              tooltip={t("preferences.reminderCount", {
                ns: "tipToolHelp",
              })}
              error={fieldErrors.eolVerificationReminderCount}
            >
              <select
                id="eolVerificationReminderCount"
                className="profile-select"
                value={formValues.eolVerificationReminderCount}
                onChange={(event) =>
                  handleFieldChange(
                    "eolVerificationReminderCount",
                    event.target.value,
                  )
                }
              >
                <option value="2">2 {t("preferences.reminders")}</option>
                <option value="5">5 {t("preferences.reminders")}</option>
                <option value="10">10 {t("preferences.reminders")}</option>
              </select>
            </FormFieldRow>

            <FormFieldRow
              id="eolVerificationReminderIntervalDays"
              label={t("preferences.reminderIntervalDays")}
              required
              isWritten={
                normalizedValues.eolVerificationReminderIntervalDays.length > 0
              }
              tooltip={t("preferences.reminderIntervalDays", {
                ns: "tipToolHelp",
              })}
              error={fieldErrors.eolVerificationReminderIntervalDays}
            >
              <select
                id="eolVerificationReminderIntervalDays"
                className="profile-select"
                value={formValues.eolVerificationReminderIntervalDays}
                onChange={(event) =>
                  handleFieldChange(
                    "eolVerificationReminderIntervalDays",
                    event.target.value,
                  )
                }
              >
                <option value="1">1 {t("preferences.day")}</option>
                <option value="2">2 {t("preferences.days")}</option>
                <option value="5">5 {t("preferences.days")}</option>
                <option value="10">10 {t("preferences.days")}</option>
                <option value="15">15 {t("preferences.days")}</option>
              </select>
            </FormFieldRow>

            <FormFieldRow
              id="minBodyWordCount"
              label={t("preferences.minimumBodyWordCount")}
              required
              isWritten={normalizedValues.minBodyWordCount.length > 0}
              tooltip={t("preferences.minBodyWordCount", {
                ns: "tipToolHelp",
              })}
              error={fieldErrors.minBodyWordCount}
            >
              <input
                id="minBodyWordCount"
                className="profile-input"
                type="number"
                min={20}
                value={formValues.minBodyWordCount}
                onChange={(event) =>
                  handleFieldChange("minBodyWordCount", event.target.value)
                }
              />
            </FormFieldRow>

            <FormFieldRow
              id="preferredLanguage"
              label={t("preferences.language")}
              required
              isWritten={normalizedValues.preferredLanguage.length > 0}
              tooltip={t("preferences.preferredLanguage", {
                ns: "tipToolHelp",
              })}
              error={fieldErrors.preferredLanguage}
            >
              <select
                id="preferredLanguage"
                className="profile-select"
                value={formValues.preferredLanguage}
                onChange={(event) =>
                  handleFieldChange("preferredLanguage", event.target.value)
                }
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </FormFieldRow>

            <FormFieldRow
              id="eolAccountReaderRecipientId"
              label={t("preferences.accountReader")}
              isWritten={
                normalizedValues.eolAccountReaderRecipientId.length > 0
              }
              tooltip={t("preferences.accountReaderRecipient", {
                ns: "tipToolHelp",
              })}
              error={fieldErrors.eolAccountReaderRecipientId}
            >
              <select
                id="eolAccountReaderRecipientId"
                className="profile-select"
                value={formValues.eolAccountReaderRecipientId}
                onChange={(event) =>
                  handleFieldChange(
                    "eolAccountReaderRecipientId",
                    event.target.value,
                  )
                }
              >
                <option value="">{t("preferences.accountReaderNone")}</option>
                {recipients.map((recipient) => (
                  <option key={recipient.id} value={recipient.id}>
                    {[recipient.firstName, recipient.lastName]
                      .filter(Boolean)
                      .join(" ")
                      .trim() ||
                      recipient.email ||
                      recipient.phone ||
                      recipient.id}
                  </option>
                ))}
              </select>
            </FormFieldRow>

            <FormFieldRow
              id="eolVerificationChannelSmsEnabled"
              label={t("preferences.smsChannel")}
              isWritten
              tooltip={t("preferences.smsVerification", {
                ns: "tipToolHelp",
              })}
            >
              <label className="profile-emailValue">
                <input
                  id="eolVerificationChannelSmsEnabled"
                  type="checkbox"
                  checked={
                    formValues.eolVerificationChannelSmsEnabled === "true"
                  }
                  onChange={(event) =>
                    handleFieldChange(
                      "eolVerificationChannelSmsEnabled",
                      event.target.checked ? "true" : "false",
                    )
                  }
                />{" "}
                {t("preferences.smsChannelHelp")}
              </label>
            </FormFieldRow>

            <FormFieldRow
              id="eolStopScheduledMessages"
              label={t("preferences.stopScheduledMessages")}
              isWritten
              tooltip={t("preferences.stopFixAfterEol", {
                ns: "tipToolHelp",
              })}
            >
              <label className="profile-emailValue">
                <input
                  id="eolStopScheduledMessages"
                  type="checkbox"
                  checked={formValues.eolStopScheduledMessages === "true"}
                  onChange={(event) =>
                    handleFieldChange(
                      "eolStopScheduledMessages",
                      event.target.checked ? "true" : "false",
                    )
                  }
                />{" "}
                {t("preferences.stopScheduledMessagesHelp")}
              </label>
            </FormFieldRow>
          </div>
        </section>
      </div>

      <ConfirmationModal
        isOpen={showUnsavedDialog}
        title={t("errorMessage.unsavedChanges")}
        message={t("errorMessage.unsavedChangesPrompt")}
        primaryLabel={t("common.discardChanges")}
        secondaryLabel={t("common.continueEdit")}
        onPrimary={handleConfirmDiscardChanges}
        onSecondary={handleContinueEditing}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
      />

      <ConfirmationModal
        isOpen={isSaveResultModalOpen}
        title={saveResultTitle}
        message={saveResultMessage}
        primaryLabel={t("common.finishEdit")}
        secondaryLabel={t("common.continueEdit")}
        onPrimary={() => {
          setIsSaveResultModalOpen(false);
          if (saveShouldReturnToDashboard) {
            navigate("/Dashboard");
          }
        }}
        onSecondary={() => setIsSaveResultModalOpen(false)}
        primaryButtonClassName="btn btn--green btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
      />
    </div>
  );
}

export default Preferences;
